import {Box, HBox} from '../../../../components/core/view';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  Divider,
} from 'native-base';
import {CustomText, H6Text} from '../../../../components/core/text';
import {
  ItemScannerAction,
  useItemScannerContext,
} from '@buncha/itemDetails/hooks/ItemScanner';
import {OrderItem, UpdatePage} from '../../../../itemDetails/types/ItemScanner';
import React, {useCallback, useEffect, useMemo} from 'react';
import {formatCurrency, isItemOrganic} from '../../../../utils/common';
import {
  useColorThemeType,
  useFontTheme,
  useShadow,
} from '../../../../appStyles/hooks/theme';

import {Accordian} from '@buncha/components/composites';
import BarcodeIcon from '@buncha/components/core/icon/BarcodeIcon';
import BuyersListForScan from '../BuyersListForScan';
import {CustomImage} from '../../../../components/core/Image';
import If from '@buncha/components/conditional/If';
import ItemName from '@buncha/batchDetails/component/ItemName';
import ItemTypePill from '@buncha/batchDetails/component/ItemName/ItemTypePill';
import {Modules} from '@buncha/utils/types';
import PriceTagToggle from '../../ItemDetail/priceTagToggle';
import SemiCircularDivider from '../../SemiCircularDivider';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {SpinnerComponent} from '@buncha/components/core/loader';
import StepText from '../StepText';
import {Touchable} from '../../../../components/core/touchable';
import {UpcStatus} from '@buncha/batchDetails/types';
import UpdateSection from '../updateSection';
import VerifyUPCPill from '../../ItemDetail/VerifyUPCPill';
import font from '../../../../appStyles/style/font';
import {styles} from './style';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';

interface ItemDetailsProps {
  title: string;
  item?: OrderItem;
  scanned?: boolean;
  openImagesBottomSheet?: Function;
  showCaptureModeFunc?: (() => Promise<void>) | (() => void);
  imageUploading?: boolean;
  isUPCMode?: boolean;
  hasUnableToScanBarcode?: boolean;
  hasPriceTagVerified?: boolean;
  isMeijerStore?: boolean;
  catalogItemId?: number;
  barcode?: string;
}

const ItemDetails = (props: ItemDetailsProps) => {
  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const {
    item,
    scanned,
    openImagesBottomSheet,
    hasUnableToScanBarcode,
    hasPriceTagVerified,
    isMeijerStore,
    barcode,
  } = props;
  const {state, dispatch} = useItemScannerContext();
  const name = item?.name;
  const price =
    item?.shopperItemChangesForShopper?.updatedPricePerQuantity || item?.price;
  const itemImageUrl = item?.itemImageUrl || '';
  const {body, button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const scannerExpanded = state.scannerExpanded;
  const buyerData = state.buyerData;
  const allFields = buyerData?.[0]?.whatPageToShow === UpdatePage.carousel;
  const isOrganic = isItemOrganic(name ?? '');
  const shadow = useShadow('2');

  const imageSaved = item?.upcStatus === UpcStatus.imageSaved;

  const expandedColor = scannerExpanded
    ? theme?.text.blue[300]
    : theme?.text.gray[100];

  const unexpandedColor = !scannerExpanded
    ? theme?.text.blue[300]
    : theme?.text.gray[100];

  const justifyContent = scannerExpanded ? 'space-between' : 'flex-start';

  const routeParams: any = useRoute().params;
  const quantity = routeParams?.quantity;
  const storeIndex = routeParams?.storeIndex;

  const singleQuantiy = state?.buyerData?.[0]?.isItemPickedOnScan;

  const toggleAccordian = useCallback(() => {
    if ((!scanned && item?.catalogItemId) || !dispatch) return;
    if (!item?.catalogItemId)
      dispatch(ItemScannerAction.setScannerExpanded(false));
    else dispatch(ItemScannerAction.setScannerExpanded(!scannerExpanded));
  }, [dispatch, item?.catalogItemId, scanned, scannerExpanded]);

  const handleImageOnPress = useCallback(() => {
    if (!props?.isUPCMode && !state.scannerExpanded && item?.catalogItemId)
      return;
    if (
      (item?.catalogItemId && openImagesBottomSheet) ||
      (props?.isUPCMode && openImagesBottomSheet)
    )
      openImagesBottomSheet();
    else if (props?.showCaptureModeFunc) props.showCaptureModeFunc();
  }, [
    item?.catalogItemId,
    openImagesBottomSheet,
    props,
    state.scannerExpanded,
  ]);

  const step2Text = useMemo(() => {
    if (!scannerExpanded) return button?.itemWeights;
    if (!allFields) return button?.updateQuantity;
    return button?.updatePriceQuantity;
  }, [
    allFields,
    button?.itemWeights,
    button?.updatePriceQuantity,
    button?.updateQuantity,
    scannerExpanded,
  ]);

  useEffect(() => {
    if (dispatch && ((scanned && !singleQuantiy) || !item?.catalogItemId))
      dispatch(ItemScannerAction.setScannerExpanded(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.catalogItemId]);

  const getText = useMemo(() => {
    if (props?.isUPCMode) return body?.upcModeText;
    else if (item?.catalogItemId)
      return body?.orderModeText?.replace('{step2Text}', step2Text);
    return step2Text;
  }, [
    body?.orderModeText,
    body?.upcModeText,
    item?.catalogItemId,
    props?.isUPCMode,
    step2Text,
  ]);

  const getImageCTAText = useMemo(() => {
    if (item?.catalogItemId && hasUnableToScanBarcode) {
      return body?.unableToScan;
    } else if (item?.isCustomItem) {
      if (item?.itemImageUrl) return button?.updatePhoto;
      return button?.takeItemPhoto;
    }
  }, [
    body?.unableToScan,
    button?.takeItemPhoto,
    button?.updatePhoto,
    hasUnableToScanBarcode,
    item?.catalogItemId,
    item?.isCustomItem,
    item?.itemImageUrl,
  ]);

  return (
    <Box
      style={[
        styles.containerStyles,
        {
          justifyContent,
          backgroundColor: theme?.background?.white?.[100],
        },
        shadow,
      ]}>
      <Box>
        <BuyersListForScan
          runId={routeParams?.runId}
          orderItemId={item?.id || 0}
          catalogItemId={item?.catalogItemId || props?.catalogItemId || 0}
        />
        <SemiCircularDivider containerStyle={styles.dividerSemiCircle} />
        <HBox style={styles.detailsContainer}>
          <If condition={!props.imageUploading}>
            <Box style={styles.imageBox}>
              <CustomImage
                style={styles.image}
                source={itemImageUrl}
                resizeMode="cover"
                onErrorSource={''}
              />
              <HBox
                style={[
                  styles.highlightText,
                  {
                    backgroundColor: theme?.background.yellow[100],
                  },
                ]}>
                <CustomText
                  style={{
                    fontFamily: muliFont[700].normal,
                    color: theme?.text.blue[300],
                  }}>{`Qty: ${quantity || 1} `}</CustomText>
              </HBox>
            </Box>
          </If>
          <If condition={props.imageUploading}>
            <SpinnerComponent size={'sm'} height={75} width={75} />
          </If>
          <Box style={styles.textContainer}>
            <If condition={!isOrganic}>
              <ItemTypePill />
            </If>
            <ItemName name={name ?? ''} />
            <HBox style={styles.footerContaienr}>
              <H6Text
                style={{
                  fontFamily: muliFont[700].normal,
                  color: theme?.text.blue[300],
                }}>
                {formatCurrency(price || 0)}
              </H6Text>
              <If condition={barcode?.length}>
                <HBox style={styles.barcodeBox}>
                  <BarcodeIcon fill={theme?.text?.blue?.[300]} />
                  <H6Text
                    style={{
                      color: theme?.text?.blue?.[300],
                    }}>
                    {barcode}
                  </H6Text>
                </HBox>
              </If>
            </HBox>
          </Box>
        </HBox>
        <If condition={hasUnableToScanBarcode}>
          <HBox style={styles.unableToScanBox}>
            <Touchable
              disabled={Boolean(item?.catalogItemId && !hasUnableToScanBarcode)}
              onPress={handleImageOnPress}>
              <CustomText
                style={[
                  styles.unableToScanText,
                  {
                    color: theme?.text.red[100],
                    fontFamily: font.fonts.muliBold,
                  },
                ]}>
                {getImageCTAText}
              </CustomText>
            </Touchable>
            <If condition={props?.isUPCMode}>
              <VerifyUPCPill />
            </If>
          </HBox>
        </If>

        <If condition={hasPriceTagVerified && !isMeijerStore}>
          <Box style={styles.priceTagBox}>
            <PriceTagToggle
              hasPriceTag={item?.priceTag || false}
              orderItemId={item?.id || 0}
              runId={routeParams?.runId || 0}
              storeIndex={storeIndex}
            />
          </Box>
        </If>
        <If condition={item?.catalogItemId && !singleQuantiy}>
          <Accordian
            disabled={
              !scanned ||
              state?.upcCrowdSourceProcessCompleted ||
              (singleQuantiy && !props.isUPCMode)
            }
            style={styles.accordian}
            onPress={toggleAccordian}
            header={
              <If condition={!singleQuantiy}>
                <StepText
                  imageSaved={imageSaved}
                  scanned={Boolean(scanned)}
                  active={scannerExpanded}
                  text={body?.stepScanBarcode}
                />
              </If>
            }
            isExpanded={scannerExpanded || singleQuantiy}
            children={<Box />}
            iconDisable={
              !props.isUPCMode && <ChevronDownIcon color={expandedColor} />
            }
            iconEnable={
              !props.isUPCMode &&
              item?.catalogItemId &&
              !singleQuantiy && <ChevronUpIcon color={expandedColor} />
            }
          />
        </If>
      </Box>
      <If condition={!scannerExpanded}>
        <Divider style={styles.divider} />
      </If>
      <If condition={!singleQuantiy}>
        <Box
          style={[
            styles.bottomCta,
            {
              backgroundColor: theme?.background.white[100],
              borderTopColor: theme?.text.gray[200],
            },
          ]}>
          <Accordian
            disabled={!scanned || state?.upcCrowdSourceProcessCompleted}
            style={styles.accordian}
            onPress={toggleAccordian}
            header={
              <StepText
                scanned={false}
                active={!scannerExpanded}
                imageSaved={imageSaved}
                text={getText}
              />
            }
            isExpanded={!scannerExpanded}
            children={
              <UpdateSection
                isUPCMode={props?.isUPCMode || false}
                isMeijerStore={isMeijerStore ?? false}
              />
            }
            iconEnable={
              !props.isUPCMode && <ChevronUpIcon color={unexpandedColor} />
            }
            iconDisable={
              !props.isUPCMode && <ChevronRightIcon color={unexpandedColor} />
            }
          />
        </Box>
      </If>
    </Box>
  );
};

export default React.memo(ItemDetails);
